import axios from 'axios';

// Use environment variable or fallback to localhost
// Job service endpoints are at /jobs/v1/*, /applications/v1/*, etc.
const JOB_SERVICE_BASE_URL = import.meta.env.VITE_JOB_SERVICE_URL || 'http://localhost:8003';

/**
 * Job interface matching Go job-service schema (camelCase)
 */
export interface Job {
    id?: string;
    _id?: string; // MongoDB ObjectID alternative
    companyId?: string;
    companyName?: string;
    title?: string;
    description?: string;
    location?: string | {
        city?: string;
        state?: string;
        country?: string;
    };
    employmentType?: string;
    experienceLevel?: string;
    requirements?: {
        requiredSkills?: string[];
        preferredSkills?: string[];
        education?: string;
        yearsExperience?: number;
    };
    salaryRange?: string;
    benefits?: string[];
    aiParsedData?: any;
    embedding?: number[];
    isActive?: boolean;
    postedBy?: string;  // User ID who posted the job (used as recruiterId for applications)
    recruiterId?: string;  // Alternative field name for postedBy
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    remote?: boolean;

    // Hard criteria (optional)
    hardCriteria?: {
        minYearsExperience?: number;
        requiredSkills?: string[];
        requiredDegrees?: string[];
    };

    // Soft criteria (optional)
    softCriteria?: {
        preferredTitles?: string[];
        preferredCompanies?: string[];
        preferredSchools?: string[];
        preferredSkills?: string[];
        keywords?: string[];
        preferMoreExperience?: boolean;
    };
}

/**
 * Response format from Go job-service API
 */
export interface GoApiResponse<T> {
    payload: T;
    status: {
        msg: string;
        error: boolean;
    };
}

export interface JobListPayload {
    data: Job[];
    pagination: {
        page: number;
        size: number;
        totalPages: number;
        totalItems: number;
    };
}

// Legacy Python format (kept for compatibility)
export interface JobListResponse {
    success: boolean;
    data: {
        count: number;
        jobs: Job[];
    };
}

/**
 * Job API Service
 * Handles all interactions with the job-service backend
 */
class JobApiService {
    private getAuthToken(): string | null {
        return localStorage.getItem('authToken');
    }

    private getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
        };
    }

    /**
     * Fetch all jobs from the job-service backend
     * @param params - Optional query parameters (limit, skills)
     * @returns Promise<Job[]>
     */
    async getAllJobs(params?: {
        limit?: number;
        page?: number;
        skills?: string;
        q?: string;
        experienceLevel?: string;
        company?: string;
    }): Promise<{ jobs: Job[]; totalItems: number; totalPages: number; page: number }> {
        try {
            const queryParams = new URLSearchParams();
            const page = params?.page || 1;
            const size = params?.limit || 12;
            queryParams.append('page', page.toString());
            queryParams.append('size', size.toString());

            if (params?.q) queryParams.append('q', params.q);
            if (params?.skills) queryParams.append('skills', params.skills);
            if (params?.experienceLevel) queryParams.append('experienceLevel', params.experienceLevel);
            if (params?.company) queryParams.append('company', params.company);

            const url = `${JOB_SERVICE_BASE_URL}/jobs/v1/list?${queryParams.toString()}`;

            const response = await axios.get<GoApiResponse<JobListPayload>>(url);

            // Go service format: { payload: { data: [...], pagination: {...} }, status: {...} }
            if (!response.data.status.error) {
                const { data, pagination } = response.data.payload;
                return {
                    jobs: data || [],
                    totalItems: pagination?.totalItems || 0,
                    totalPages: pagination?.totalPages || 1,
                    page: pagination?.page || page,
                };
            } else {
                throw new Error(response.data.status.msg || 'Failed to fetch jobs');
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
            throw error;
        }
    }

    /**
     * Fetch a single job by ID
     * @param jobId - The job ID
     * @returns Promise<Job>
     */
    async getJobById(jobId: string): Promise<Job> {
        try {
            const response = await axios.get<GoApiResponse<Job>>(
                `${JOB_SERVICE_BASE_URL}/jobs/v1/${jobId}`
            );

            // Go service returns the job directly in payload
            if (!response.data.status.error) {
                return response.data.payload;
            } else {
                throw new Error(response.data.status.msg || 'Failed to fetch job');
            }
        } catch (error) {
            console.error('Error fetching job by ID:', error);
            throw error;
        }
    }

    /**
     * Search jobs by skills
     * @param params - Search parameters (skills)
     * @returns Promise<Job[]>
     */
    async searchJobs(params: { skills?: string }): Promise<Job[]> {
        const result = await this.getAllJobs(params);
        return result.jobs;
    }

    /**
     * Get job count
     * @returns Promise<number>
     */
    async getJobCount(): Promise<number> {
        try {
            const response = await axios.get<GoApiResponse<JobListPayload>>(
                `${JOB_SERVICE_BASE_URL}/jobs/v1/list?page=1&size=1`
            );

            if (!response.data.status.error) {
                return response.data.payload.pagination.totalItems;
            } else {
                throw new Error(response.data.status.msg || 'Failed to fetch job count');
            }
        } catch (error) {
            console.error('Error fetching job count:', error);
            throw error;
        }
    }

    /**
     * Create a new job posting
     * @param jobData - The job data to create
     * @returns Promise<Job>
     */
    async createJob(jobData: Partial<Job>): Promise<Job> {
        try {
            // Transform data to match Go service schema (camelCase)
            const payload = {
                title: jobData.title,
                companyName: jobData.companyName,
                companyId: jobData.companyId,
                description: jobData.description || '',
                location: typeof jobData.location === 'string' ? jobData.location : '',
                employmentType: jobData.employmentType || '',
                salaryRange: jobData.salaryRange || '',
                requirements: jobData.requirements || { requiredSkills: [] },
                isActive: true,
                postedBy: jobData.postedBy || jobData.recruiterId || ''
            };

            const response = await axios.post<GoApiResponse<{ id: string; message: string }>>(
                `${JOB_SERVICE_BASE_URL}/jobs/v1/create`,
                payload
            );

            if (!response.data.status.error) {
                // Fetch the created job to return full object
                return await this.getJobById(response.data.payload.id);
            } else {
                throw new Error(response.data.status.msg || 'Failed to create job');
            }
        } catch (error) {
            console.error('Error creating job:', error);
            throw error;
        }
    }

    /**
     * Update job status (e.g., from 'draft' to 'active')
     * @param jobId - The job ID
     * @param status - The new status ('draft', 'active', 'closed', etc.)
     * @returns Promise<Job>
     */
    async updateJobStatus(jobId: string, status: string): Promise<Job> {
        try {
            const response = await axios.patch<GoApiResponse<{ message: string }>>(
                `${JOB_SERVICE_BASE_URL}/jobs/v1/${jobId}`,
                { isActive: status === 'active' }
            );

            if (!response.data.status.error) {
                // Fetch updated job to return full object
                return await this.getJobById(jobId);
            } else {
                throw new Error(response.data.status.msg || 'Failed to update job status');
            }
        } catch (error) {
            console.error('Error updating job status:', error);
            throw error;
        }
    }

    /**
     * Update job details — writes to write-layer fields (root + hardCriteria + softCriteria).
     * Backend recomputes read_model and matching_fields after update, then diffs for embedding regen.
     * @param jobId - The job ID
     * @param updates - Partial job data to update
     * @returns Promise<Job>
     */
    async updateJob(jobId: string, updates: Partial<JobEditUpdate>): Promise<Job> {
        try {
            const payload: Record<string, any> = {};

            // Root-level write-layer fields (NormalizeJSONKeys converts camelCase → BSON)
            if (updates.title !== undefined) payload['title'] = updates.title;
            if (updates.description !== undefined) payload['description'] = updates.description;
            if (updates.location !== undefined) payload['location'] = updates.location;
            if (updates.employmentType !== undefined) payload['employmentType'] = updates.employmentType;
            if (updates.experienceLevel !== undefined) payload['experienceLevel'] = updates.experienceLevel;
            if (updates.salaryRange !== undefined) payload['salaryRange'] = updates.salaryRange;
            if (updates.benefits !== undefined) payload['benefits'] = updates.benefits;

            // Hard criteria — BSON dot notation for partial sub-field updates
            if (updates.requiredSkills !== undefined) payload['hard_criteria.required_skills'] = updates.requiredSkills;
            if (updates.minYearsExperience !== undefined) payload['hard_criteria.min_years_experience'] = updates.minYearsExperience;
            if (updates.requiredDegrees !== undefined) payload['hard_criteria.required_degrees'] = updates.requiredDegrees;
            if (updates.requiredCertifications !== undefined) payload['hard_criteria.required_certifications'] = updates.requiredCertifications;

            // Soft criteria — BSON dot notation for partial sub-field updates
            if (updates.preferredSkills !== undefined) payload['soft_criteria.preferred_skills'] = updates.preferredSkills;
            if (updates.keywords !== undefined) payload['soft_criteria.keywords'] = updates.keywords;

            // Display-only fields (no write-layer struct — kept in their current locations)
            if (updates.education !== undefined) payload['requirements.education'] = updates.education;
            if (updates.responsibilities !== undefined) payload['ai_parsed_data.responsibilities'] = updates.responsibilities;
            if (updates.workMode !== undefined) {
                payload['remote'] = updates.workMode === 'Remote';
                payload['ai_parsed_data.work_mode'] = updates.workMode;
            }

            const response = await axios.patch<GoApiResponse<{ message: string }>>(
                `${JOB_SERVICE_BASE_URL}/jobs/v1/${jobId}/update`,
                payload,
                { headers: this.getAuthHeaders() }
            );

            if (!response.data.status.error) {
                return await this.getJobById(jobId);
            } else {
                throw new Error(response.data.status.msg || 'Failed to update job');
            }
        } catch (error) {
            console.error('Error updating job:', error);
            throw error;
        }
    }
}

/**
 * JobEditUpdate — fields editable via the frontend job detail page.
 * Writes target the write layer (root fields + hardCriteria + softCriteria).
 */
export interface JobEditUpdate {
    title?: string;
    description?: string;
    location?: string;
    employmentType?: string;
    experienceLevel?: string;
    salaryRange?: string;
    requiredSkills?: string[];
    preferredSkills?: string[];
    keywords?: string[];
    minYearsExperience?: number;
    requiredDegrees?: string[];
    requiredCertifications?: string[];
    education?: string;
    benefits?: string[];
    responsibilities?: string[];
    workMode?: string;
}

export const jobApiService = new JobApiService();
